import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

/**
 * StatusDot — Industrial Display Primitive (F3, TES-950)
 *
 * Kleiner runder Status-Indikator (Stitch screen-04:298/312/340, screen-05:309/329/349).
 * Reine Praesentation. Mock-Klassen sind auf reale Projekt-Token gemappt:
 *   - pass    bg-[#1b5e20] (bewusstes Mock-Literal "Pass"-Gruen, vgl. screen-04) ~ bg-success
 *   - fail    bg-destructive animate-pulse (Fail/Overdue, pulsiert)
 *   - pending bg-secondary (Safety-Yellow; Mock bg-secondary-container existiert NICHT als Token)
 *   - scheduled bg-muted-foreground (Incomplete/Not-Started)
 *
 * Mapping auf reale defect_status-Werte (Anzeige-Hinweis, KEINE Schema-Behauptung):
 *   resolved/closed -> pass · open -> fail (offen = aufmerksamkeitsbeduerftig) ·
 *   in_progress -> pending · cancelled -> scheduled/neutral.
 */
const statusDotVariants = cva("inline-block rounded-full shrink-0", {
  variants: {
    state: {
      pass: "bg-success",
      fail: "bg-destructive animate-pulse",
      pending: "bg-secondary",
      scheduled: "bg-muted-foreground",
    },
    size: {
      sm: "w-2 h-2",
      md: "w-2.5 h-2.5",
    },
  },
  defaultVariants: {
    state: "pending",
    size: "sm",
  },
});

interface StatusDotProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusDotVariants> {
  /** Optionales sichtbares Label rechts neben dem Dot (i18n-Text vom Aufrufer). */
  label?: React.ReactNode;
}

const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, state, size, label, ...props }, ref) => {
    if (label === undefined) {
      return (
        <span
          ref={ref}
          className={cn(statusDotVariants({ state, size }), className)}
          aria-hidden="true"
          {...props}
        />
      );
    }
    return (
      <span ref={ref} className={cn("inline-flex items-center gap-2", className)} {...props}>
        <span className={cn(statusDotVariants({ state, size }))} aria-hidden="true" />
        <span className="text-data-tabular">{label}</span>
      </span>
    );
  },
);
StatusDot.displayName = "StatusDot";

export { StatusDot };
